import React, { useState } from 'react';
import jwtDecode from 'jwt-decode';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useNavigate } from 'react-router-dom';
import { Modal } from 'react-bootstrap';

import { signIn } from '../services/auth';
import { getProfile } from '../services/user';

import { session } from '../signals/session';
import { myProfile } from '../signals/profile';

import { persistAuthSession, scheduleRefreshToken } from '../utils/session';
import TurnstileComponent from '../components/TurnstileComponent';
import i18n from '../utils/i18n';
import { creator, hasActivePlan, isCreator } from '../signals/creator';
import { getCreator, getPlansByCreatorId } from '../services/creator';
import CreatorAccountStatus from '../enums/CreatorAccountStatus';
import {
  trackAuthentication,
  setUserId,
  UserType,
  SubscriptionLevel,
  trackFunnelStep,
  trackError,
} from '../utils/analytics';

import Button from '../components/Button';

type FormValues = {
  email: string;
  password: string;
};

const Login = () => {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showLoginError, setShowLoginError] = useState(false);
  const [isTurnstileSuccess, setIsTurnstileSuccess] = useState(false);

  const validationSchema = Yup.object().shape({
    email: Yup.string()
      .email(i18n.t('Login.form.errors.emailAddressValidEmail'))
      .required(i18n.t('Login.form.errors.required')),
    password: Yup.string().required(i18n.t('Login.form.errors.required')),
  });

  const onSubmit = async (values: FormValues) => {
    try {
      setLoading(true);

      // Rastreia etapa 2 do funil de autenticação (login iniciado)
      trackFunnelStep('auth', 2, 'login_initiated', {
        has_email: !!values.email,
        has_password: !!values.password,
      });

      const { token, refreshToken } = await signIn(
        values.email.toLowerCase(),
        values.password
      );

      const { userId, exp, username } = jwtDecode<JWT>(token);

      session.value.token = token;
      session.value.refreshToken = refreshToken;
      session.value.userId = userId;
      session.value.username = username;

      persistAuthSession({
        token,
        refreshToken,
        userId,
        username,
        expires: exp,
      });

      scheduleRefreshToken();

      const profileResp = await getProfile();

      if (profileResp.creatorId) {
        const creatorResp = await getCreator();
        creator.value = creatorResp;
        isCreator.value = creatorResp?.status === CreatorAccountStatus.APPROVED;

        const plan = await getPlansByCreatorId(profileResp.creatorId);
        if (plan) {
          hasActivePlan.value = true;
        }
      }

      myProfile.value = profileResp;

      // Determina o tipo de usuário e status de assinatura para Analytics
      let userType = UserType.REGISTERED;
      let subscriptionStatus = SubscriptionLevel.NONE;

      if (isCreator.value) {
        userType = UserType.CREATOR;
        if (hasActivePlan.value) {
          subscriptionStatus = SubscriptionLevel.ACTIVE;
        }
      } else if (hasActivePlan.value) {
        userType = UserType.SUBSCRIBER;
        subscriptionStatus = SubscriptionLevel.ACTIVE;
      }

      // Track successful login in Google Analytics
      trackAuthentication('login', true);

      // Set user ID for cross-device tracking with dados avançados
      setUserId(userId, userType, subscriptionStatus, {
        has_profile_image: !!profileResp.profile?.avatar,
        has_profile_cover: !!profileResp.profile?.cover,
        login_timestamp: new Date().toISOString(),
      });

      // Rastreia etapa 3 do funil de autenticação (login concluído com sucesso)
      trackFunnelStep('auth', 3, 'login_success', {
        user_type: userType,
        subscription_status: subscriptionStatus,
        is_creator: isCreator.value,
      });

      navigate(isCreator.value ? '/myprofile' : '/');
    } catch (error) {
      // Track failed login attempt
      trackAuthentication('login', false);

      // Rastreia falha no funil de autenticação
      trackFunnelStep('auth', 2, 'login_failed', {
        error_message: error instanceof Error ? error.message : 'Unknown error',
      });

      // Rastreia o erro específico
      trackError('api', 'Login failed', false, 'Login');

      setShowLoginError(true);
    } finally {
      setLoading(false);
    }
  };

  const {
    handleSubmit,
    handleChange,
    handleBlur,
    setFieldValue,
    isValid,
    errors,
    touched,
    values,
  } = useFormik({
    initialValues: { email: '', password: '' },
    validationSchema,
    onSubmit,
    validateOnBlur: false,
  });

  return (
    <>
      <div className="login-container">
        <div className="container-fluid h-100">
          <div className="row h-100 align-items-center justify-content-center">
            <div className="col-12 col-md-8 col-lg-6 col-xl-5">
              <div className="login-card">
                <div className="login-logo-container">
                  <img
                    src="assets/images/logo-only.png"
                    className="login-logo"
                    alt="Silk Secret"
                  />
                  <h1 className="login-brand-name">silk secret</h1>
                </div>

                <h2 className="login-title">{i18n.t<string>('Login.title')}</h2>

                <form onSubmit={handleSubmit} className="login-form">
                  <div className="input-group">
                    <i className="ti-email input-icon"></i>
                    <input
                      type="text"
                      id="email"
                      autoCapitalize="none"
                      className="form-input"
                      placeholder={i18n.t<string>('Login.form.email')}
                      inputMode="email"
                      onBlur={handleBlur}
                      onChange={handleChange}
                      value={values.email}
                    />
                    {touched.email && errors.email && (
                      <div className="input-error">
                        <i className="feather-alert-circle"></i>
                        {errors.email}
                      </div>
                    )}
                  </div>

                  <div className="input-group">
                    <i className="ti-lock input-icon"></i>
                    <input
                      type={showPassword ? 'text' : 'password'}
                      className="form-input"
                      placeholder={i18n.t<string>('Login.form.password')}
                      id="password"
                      autoCapitalize="none"
                      onChange={(e) => {
                        const text = e.target.value;
                        setFieldValue('password', text.trim());
                      }}
                      onBlur={handleBlur}
                      value={values.password}
                    />
                    <button
                      type="button"
                      className="password-toggle"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      <i
                        className={`feather-${
                          showPassword ? 'eye' : 'eye-off'
                        }`}
                      ></i>
                    </button>
                    {touched.password && errors.password && (
                      <div className="input-error">
                        <i className="feather-alert-circle"></i>
                        {errors.password}
                      </div>
                    )}
                  </div>

                  <div className="login-options justify-content-center">
                    {/* <div className="remember-me">
                      <input type="checkbox" id="remember" />
                      <label htmlFor="remember">Lembrar de mim</label>
                    </div> */}
                    <a href="/forgot" className="forgot-link">
                      {i18n.t<string>('Login.forgot')}
                    </a>
                  </div>

                  {!isTurnstileSuccess && (
                    <div className="d-flex justify-content-center">
                      <TurnstileComponent
                        onSuccess={() => setIsTurnstileSuccess(true)}
                        onError={() => setIsTurnstileSuccess(false)}
                      />
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={!isValid || !isTurnstileSuccess || loading}
                    className="btn-login"
                  >
                    {loading ? (
                      <div className="login-loading">
                        <div className="spinner"></div>
                        <span>Entrando...</span>
                      </div>
                    ) : (
                      i18n.t<string>('Login.button')
                    )}
                  </button>
                </form>

                <div className="signup-link">
                  {i18n.t<string>('Login.noAccount')}
                  <a href="/register" style={{ marginLeft: 5 }}>
                    {i18n.t<string>('Login.register')}
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Modal show={showLoginError}>
        <Modal.Header
          closeButton
          onClick={() => setShowLoginError(false)}
          className="bg-white"
          style={{
            border: 0,
            borderRadius: 0,
            padding: 20,
            borderTopLeftRadius: 10,
            WebkitBorderTopRightRadius: 10,
          }}
        />
        <Modal.Body
          className="bg-white"
          style={{
            borderBottomLeftRadius: 10,
            borderBottomRightRadius: 10,
          }}
        >
          <p className="font-xs text-black">
            {i18n.t<string>('Login.loginFailure')}
          </p>
          <div className="form-group mb-1">
            <Button
              onClick={() => setShowLoginError(false)}
              disabled={!isValid}
            >
              {i18n.t<string>('Global.ok')}
            </Button>
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default Login;
